import { Extension, Editor } from '@tiptap/core';
import { Node as TiptapNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';
import { ReactRenderer } from '@tiptap/react';
import tippy, { Instance, Props } from 'tippy.js';
import { CommentHoveringButton } from './CommentHoveringButton';

const shouldDiveNodes = new Set(['doc', 'blockThread']);

const checkIsEmptyBlock = (node: TiptapNode) => {
  switch (node.type.name) {
    case 'paragraph':
      return node.content.size < 1;
    default:
      return false;
  }
};

const findNodeUnderCursor = (
  view: EditorView,
  event: MouseEvent,
  allowedNodes: string[],
  shouldDiveIntoNodes: string[]
) => {
  const coords = { left: event.clientX, top: event.clientY };

  const element = document.elementFromPoint(coords.left, coords.top);
  if (!element) return null;

  const posInfo = view.posAtCoords(coords);
  if (!posInfo) return null;

  const $pos = view.state.doc.resolve(posInfo.pos);

  const nodes = [];

  const nodeAtPos = view.state.doc.nodeAt(posInfo.pos);
  if (nodeAtPos && !nodeAtPos.isText) {
    nodes.push({ node: nodeAtPos, pos: posInfo.pos, priority: $pos.depth });
  }

  if ($pos.nodeBefore && !$pos.nodeBefore.isText) {
    let updatedPos = posInfo.pos - 1;
    view.state.doc.nodesBetween(updatedPos, updatedPos, (node, pos) => {
      if (node === $pos.nodeBefore) {
        updatedPos = pos;
      }
    });
    nodes.push({
      node: $pos.nodeBefore,
      pos: updatedPos,
      priority: view.state.doc.resolve(posInfo.pos - 1).depth,
    });
  }

  // For atomic nodes
  if (
    nodeAtPos &&
    nodeAtPos.isAtom &&
    $pos.nodeBefore &&
    $pos.nodeBefore.isAtom
  ) {
    const beforePos = posInfo.pos - $pos.nodeBefore.nodeSize;

    let keepNode = null;
    let keepPos = null;

    try {
      const beforeDom = view.nodeDOM(beforePos);
      if (beforeDom) {
        const beforeRect = (beforeDom as HTMLElement).getBoundingClientRect();
        if (
          coords.left >= beforeRect.left &&
          coords.left <= beforeRect.right &&
          coords.top >= beforeRect.top &&
          coords.top <= beforeRect.bottom
        ) {
          keepNode = $pos.nodeBefore;
          keepPos = beforePos;
        }
      }

      const afterDom = view.nodeDOM(posInfo.pos);
      if (afterDom && !keepNode) {
        const afterRect = (afterDom as HTMLElement).getBoundingClientRect();
        if (
          coords.left >= afterRect.left &&
          coords.left <= afterRect.right &&
          coords.top >= afterRect.top &&
          coords.top <= afterRect.bottom
        ) {
          keepNode = nodeAtPos;
          keepPos = posInfo.pos;
        }
      }

      nodes.length = nodes.length - 2;
      if (keepNode) {
        nodes.push({ node: keepNode, pos: keepPos, priority: 2 });
      }
    } catch (e) {
      // Nothing to handle here
    }
  }

  for (let depth = 0; depth <= $pos.depth; depth++) {
    const node = $pos.node(depth);
    if (!node.isText) {
      const pos = depth === 0 ? 0 : $pos.before(depth);
      nodes.push({ node, pos, priority: depth }); // Use positive depth
    }
  }

  const parentNodes = nodes
    .filter((n) => n.priority >= 0)
    .sort((a, b) => a.priority - b.priority);

  for (const { node, pos } of parentNodes) {
    if (allowedNodes.includes(node.type.name)) {
      if (!shouldDiveIntoNodes.includes(node.type.name)) {
        if (checkIsEmptyBlock(node)) return null;
        return { node, pos };
      }
    }
  }

  return null;
};

export const commentHoveringBlockPluginKey =
  new PluginKey<CommentHoveringBlockPluginState>('commentHoveringBlock');

export interface CommentHoveringExtensionOptions {
  tags?: string[];
  tippyOptions?: Partial<Props>;
  buttonWidth?: number;
  rightOffset?: number;
  onClick?: () => void;
}

export type CommentHoveringBlockPluginState = {
  hoveredBlockNode: TiptapNode | null;
  hoveredBlockPosition: number | null;
};

class CommentHoveringBlockView {
  public editor: Editor;
  public element: HTMLElement;
  public tippy: Instance | undefined;
  public tippyOptions?: Partial<Props>;
  public buttonWidth: number;
  public rightOffset: number;
  public reactRenderer: ReactRenderer | null;
  public onClick?: () => void;

  private blockPosition: number | null;
  private tippyMouseLeaveHandler: () => void;
  private scrollPageHandler: () => void;

  constructor({
    editor,
    tippyOptions = {},
    buttonWidth = 36,
    rightOffset = 0,
    onClick = () => null,
  }: {
    editor: any;
    tippyOptions?: Partial<Props>;
    buttonWidth?: number;
    rightOffset?: number;
    onClick?: () => void;
  }) {
    this.editor = editor;
    this.element = document.createElement('div');
    this.tippyOptions = tippyOptions;
    this.blockPosition = null;
    this.tippyMouseLeaveHandler = this.hide.bind(this);
    this.scrollPageHandler = this.updateTippyPosition.bind(this);
    this.buttonWidth = buttonWidth;
    this.rightOffset = rightOffset;
    this.onClick = onClick;
    this.reactRenderer = null;

    this.element.style.visibility = 'visible';
    this.element.style.padding = '0 8px';
    this.renderButton();
  }

  renderButton() {
    if (this.reactRenderer) return;

    const handleClick = () => {
      if (this.blockPosition !== null) {
        // Set selection to the entire block
        const node = this.editor.state.doc.nodeAt(this.blockPosition);
        if (node) {
          this.editor
            .chain()
            .focus()
            .setNodeSelection(this.blockPosition)
            .createPendingThread()
            .run();

          if (typeof this.onClick === 'function') this.onClick();
        }
      }

      this.hide();
    };

    this.reactRenderer = new ReactRenderer(CommentHoveringButton, {
      props: {
        onClick: handleClick,
      },
      editor: this.editor,
    });
    this.element.appendChild(this.reactRenderer.element);
  }

  createTooltip() {
    const { element: editorElement } = this.editor.options;
    const editorIsAttached = !!editorElement.parentElement;

    if (this.tippy || !editorIsAttached) {
      return;
    }

    this.tippy = tippy(editorElement as HTMLElement, {
      duration: 0,
      getReferenceClientRect: null,
      content: this.element,
      interactive: true,
      trigger: 'manual',
      placement: 'right-start',
      hideOnClick: 'toggle',
      offset: [0, this.rightOffset],
      popperOptions: {
        strategy: 'fixed',
        modifiers: [
          { name: 'flip', enabled: false },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              altBoundary: false,
            },
          },
        ],
      },
      zIndex: 10,
      ...this.tippyOptions,
    });
  }

  show(blockPosition: number) {
    if (!this.editor.isEditable) {
      return;
    }

    this.blockPosition = blockPosition;
    this.createTooltip();
    this.tippy?.setProps({
      getReferenceClientRect: () => this.getBlockCoordinates(blockPosition),
    });
    this.tippy?.show();
    this.tippy?.popper.addEventListener(
      'mouseleave',
      this.tippyMouseLeaveHandler
    );
    document.addEventListener('scroll', this.scrollPageHandler);
  }

  hide() {
    this.tippy?.hide();
    this.tippy?.popper.removeEventListener(
      'mouseleave',
      this.tippyMouseLeaveHandler
    );
    document.removeEventListener('scroll', this.scrollPageHandler);
  }

  updateTippyPosition() {
    if (!this.editor.isEditable) {
      this.hide();
      return;
    }

    if (this.blockPosition !== null) {
      this.tippy?.setProps({
        getReferenceClientRect: () =>
          this.getBlockCoordinates(this.blockPosition as number),
      });
    }
  }

  private getBlockCoordinates(blockPosition: number) {
    const coordinates = this.editor.view.coordsAtPos(blockPosition);
    const left = this.editor.view.dom.getBoundingClientRect().right;
    const top = coordinates.top;

    return new DOMRect(left, top, 0, 0);
  }

  destroy() {
    if (this.reactRenderer) {
      this.reactRenderer.destroy();
      this.reactRenderer = null;
    }
    this.tippy?.destroy();
    this.tippy = undefined;
  }
}

export const CommentHoveringBlockPlugin =
  Extension.create<CommentHoveringExtensionOptions>({
    name: 'commentHoveringBlockPlugin',

    addOptions() {
      return {
        tags: ['paragraph', 'heading', 'blockquote', 'listItem'],
        tippyOptions: {},
        buttonWidth: 36,
        rightOffset: 0,
        onClick: () => null,
      };
    },

    addProseMirrorPlugins() {
      const options = this.options;
      const editor = this.editor;
      let commentHoveringBlockView: CommentHoveringBlockView | null = null;

      return [
        new Plugin<CommentHoveringBlockPluginState>({
          key: commentHoveringBlockPluginKey,
          state: {
            init() {
              return { hoveredBlockNode: null, hoveredBlockPosition: null };
            },
            apply(tr, state) {
              const newPluginState = tr.getMeta(commentHoveringBlockPluginKey);

              if (newPluginState) {
                return newPluginState;
              }

              return state;
            },
          },

          view() {
            // Defer the creation to avoid React lifecycle issues
            const createView = () => {
              commentHoveringBlockView = new CommentHoveringBlockView({
                editor,
                tippyOptions: options.tippyOptions,
                buttonWidth: options.buttonWidth,
                rightOffset: options.rightOffset,
                onClick: options.onClick,
              });
            };

            // Use requestAnimationFrame to ensure we're outside any React render cycle
            requestAnimationFrame(createView);
            return {
              destroy() {
                if (commentHoveringBlockView) {
                  commentHoveringBlockView.destroy();
                  commentHoveringBlockView = null;
                }
              },
            };
          },

          props: {
            handleDOMEvents: {
              mousemove(view, event) {
                if (!editor.isEditable || !commentHoveringBlockView) {
                  if (
                    commentHoveringBlockView &&
                    commentHoveringBlockView.tippy?.state.isShown
                  ) {
                    commentHoveringBlockView.hide();
                  }
                  return;
                }

                const foundedNode = findNodeUnderCursor(
                  view,
                  event,
                  options.tags || [],
                  [...shouldDiveNodes]
                );

                if (foundedNode && foundedNode.pos !== null) {
                  view.dispatch(
                    view.state.tr.setMeta(commentHoveringBlockPluginKey, {
                      hoveredBlockNode: foundedNode.node,
                      hoveredBlockPosition: foundedNode.pos,
                    })
                  );

                  commentHoveringBlockView.show(foundedNode.pos);
                }
              },
              mouseout: (view, event) => {
                if (!editor.isEditable || !commentHoveringBlockView) {
                  if (
                    commentHoveringBlockView &&
                    commentHoveringBlockView.tippy?.state.isShown
                  ) {
                    commentHoveringBlockView.hide();
                  }
                  return;
                }

                if (
                  !commentHoveringBlockView.tippy ||
                  commentHoveringBlockView.tippy.popper.contains(
                    event.relatedTarget as Node
                  )
                ) {
                  return;
                }

                const nodePosition = view.posAtCoords({
                  left: event.clientX,
                  top: event.clientY,
                });
                const state = commentHoveringBlockPluginKey.getState(
                  view.state
                );

                if (
                  state?.hoveredBlockPosition &&
                  state.hoveredBlockNode &&
                  nodePosition &&
                  nodePosition.pos >= state.hoveredBlockPosition &&
                  nodePosition.pos <=
                    state.hoveredBlockPosition + state.hoveredBlockNode.nodeSize
                ) {
                  return;
                }

                commentHoveringBlockView.hide();
                view.dispatch(
                  view.state.tr.setMeta(commentHoveringBlockPluginKey, {
                    hoveredBlock: null,
                    hoveredBlockPosition: null,
                  })
                );
              },
            },
          },
        }),
      ];
    },
  });
